/*!
 * ThermoAspen Sauna Designer — Summary v1.6.2
 * Stage 7 of the mobile rebuild (26 Oct 2026).
 *
 * "Your sauna": a 3D construction view the customer can step through and tap (part names
 * only — no dimensions), a specification of the design (dimensions, roof, exterior, windows
 * and doors, stove, inside; each heading links to where it is set), Share and Construction drawings.
 *
 * The 3D construction view is the Construction Drawings page's own Frame 3D viewer
 * (Structural Frame Viewer 6.x), loaded in a same-origin frame only while the Summary is
 * open. Before it loads, the current design is written to the working project with
 * ThermoAspenProjectHandoff.captureCurrent() — the same step the "Construction drawings"
 * button uses — so the view always matches the design. On that page the frame shows only
 * the 3D viewer; the issue toggle, stud-spacing control, step bar and the dimension rows of
 * the "Selected piece" panel are hidden. Nothing on the Construction page is changed.
 *
 * 1.3.0: mount(host) draws the Summary inside the shell's Summary step instead of a full-screen
 * layer. open()/close() then load and release the 3D view; the step bar and bottom bar do the rest.
 *
 * 1.4.2: "Complete" (and the first view) is the step that shows the most layers, the latest
 * one if several tie. The last step can be a stove/flue close-up that hides the walls and roof
 * (e.g. when the flue leaves through a wall), so "last" was not always the finished sauna.
 *
 * 1.6.2 (3 Nov 2026): Inside reads the construction layers from layers.byId. Since TA Commercial Phase 3 the
 *   construction model comes from the Designer server, which sends each layer once (byId) without the duplicate
 *   layers.layers list, so Inside showed the frame only (no OSB, insulation, VCL, battens or counter battens).
 * 1.6.1: Inside, the ceiling counter battens and battens come before the wall counter battens and battens.
 * 1.6.0: Outside / Inside switch. Inside shows the Interior viewer itself (same look, same camera spots and
 *   navigation) in build-up mode: the frame with its OSB, insulation, VCL, battens and counter battens (taken from the
 *   Construction page model already loaded for the Outside view), then the viewer's own ceiling and wall cladding,
 *   window boards, benches, backrests and stove, step by step with the same arrows.
 * 1.5.2: the arrows cycle (after the last step comes the first, and back). Arrows keep a fixed
 *   position whatever the title length; the 3D view is taller and fits with the step controls
 *   between the top and bottom bars.
 * 1.5.1: the Frame / Walls / Roof / Complete buttons are gone. The build step is shown as a larger
 *   title (with "Step 14 of 29" under it) between two larger arrows placed right beside it.
 * 1.5.0: on wide screens (1024 px+) the 3D view sits beside the specification and stays in view.
 * Also: the Construction page is slimmed before it is shown in the frame: the site header,
 * footer, cookie banner, analytics, checkout preloading and theme shop scripts are removed;
 * only the page's own Construction blocks (and their styles) are kept. Less to download and
 * run, and no duplicate analytics page view or console errors from the header in a frame.
 *
 * Public API: window.ThermoAspenDesignerSummaryV1 { version, mount(host), open(), close(), isOpen() }
 */
(function (W, D) {
  'use strict';
  if (W.ThermoAspenDesignerSummaryV1) return;

  var VERSION = '1.6.2';
  var CONSTRUCTION_URL = '/pages/construction-test-page';
  var LOAD_TIMEOUT_MS = 120000;
  // Milestones in the Construction page's build sequence (by step id; the view is cumulative).
  var MILESTONES = [
    // 1.4.1: follows the Construction Sequence 1.8.0 order (roof framing comes straight after the walls).
    { label: 'Frame', id: 'ceiling-timbers' },
    { label: 'Walls', id: 'external-wall-cladding' },
    { label: 'Roof', id: 'roof-flashings' },
    { label: 'Complete', id: null } // null = the fullest build (see fullestIndex)
  ];
  var WALL_NAMES = { front: 'Front wall', back: 'Back wall', left: 'Left wall', right: 'Right wall' };

  var S = { mode: 'outside', inside: { steps: [], index: 0, data: null, key: null, home: null }, open: false, frame: null, viewer: null, doc: null, steps: [], index: -1, loadTimer: 0, observer: null, returnFocus: null, generation: 0 };
  var ui = {};

  function el(tag, cls, text) { var e = D.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
  function n(v, f) { v = Number(v); return isFinite(v) ? v : (f == null ? 0 : f); }
  function btn(cls, html, label) { var b = el('button', cls); b.type = 'button'; b.innerHTML = html; if (label) b.setAttribute('aria-label', label); return b; }
  var ICON = {
    info: '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v5M12 8h.01"></path></svg>',
    prev: '<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 6l-6 6 6 6"></path></svg>',
    next: '<svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"></path></svg>',
    share: '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"></path></svg>',
    chev: '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"></path></svg>'
  };
  function infoBtn(key, label) { var b = btn('ta-sum-info', ICON.info, label); b.setAttribute('data-ta-info', key); return b; }

  /* ---------- specification (information only; each heading links to where it is set) ---------- */
  function selText(q) { var s = D.querySelector(q); return s && s.options && s.options[s.selectedIndex] ? s.options[s.selectedIndex].text : ''; }
  function mm(v) { v = Number(v); return isFinite(v) && v > 0 ? Math.round(v) + ' mm' : ''; }
  function mm2(a, b) { a = Number(a); b = Number(b); return isFinite(a) && isFinite(b) && a > 0 && b > 0 ? Math.round(a) + ' × ' + Math.round(b) + ' mm' : ''; }
  function m3(v) { v = Number(v); return isFinite(v) && v > 0 ? (Math.round(v * 10) / 10).toFixed(1) + ' m³' : ''; }
  function tryGet(f) { try { return f(); } catch (e) { return null; } }
  var ROOF_TYPES = { 'pitched-gable': 'Pitched (gable)', gable: 'Pitched (gable)', monopitch: 'Monopitch', 'mono-pitch': 'Monopitch', flat: 'Flat, 1:40 fall', 'flat-roof': 'Flat, 1:40 fall' };
  function row(label, value, sub) { return value ? { label: label, value: value, sub: sub || '' } : null; }
  function sections() {
    var d = W.ThermoAspenSaunaDesignerData || {}, dim = d.dimensions || {}, bm = d.buildingMetrics || {}, roof = bm.roof || {};
    var L = n(dim.lengthMm), Wd = n(dim.widthMm), H = n(dim.heightMm);
    var E = W.ThermoAspenHeaterSystemEngine, Cat = W.ThermoAspenHeaterCatalogue;
    var core = tryGet(function () { return E && E.resolveCurrentCore ? E.resolveCurrentCore() : null; });
    var eff = core && core.analysis && core.analysis.effectiveVolumeM3;
    if (!(eff > 0)) eff = tryGet(function () { var a = E.analyse({ project: d.project }); return a && a.effectiveVolumeM3; });
    var out = [];

    /* Dimensions */
    var dimsRows = [
      row('Internal length', mm(L)), row('Internal width', mm(Wd)), row('Internal height', mm(H)),
      row('Room volume', m3(L * Wd * H / 1e9)),
      row('Effective volume', m3(eff), 'used to size the stove')
    ];
    dimsRows.push(row('External length', mm(bm.externalLengthMm), 'outside of the wall cladding'));
    dimsRows.push(row('External width', mm(bm.externalWidthMm), 'outside of the wall cladding'));
    if (roof.metalAxisLengthMm && isFinite(roof.spanMinEaveMm) && isFinite(roof.spanMaxEaveMm)) {
      var along = n(roof.metalAxisLengthMm), across = n(roof.spanMaxEaveMm) - n(roof.spanMinEaveMm);
      var lenAxis = roof.ridgeAxis === 'z' ? across : along, widAxis = roof.ridgeAxis === 'z' ? along : across;
      dimsRows.push(row('Overall with roof', mm2(lenAxis, widAxis), 'length × width, including overhangs'));
    }
    dimsRows.push(row('Maximum height', mm(bm.heightAboveFflMm), 'above finished floor level'));
    out.push({ title: 'Dimensions', go: function () { shellRow('layout', '[data-ta-layout-open="room"]'); }, rows: dimsRows });

    /* Roof */
    var colour = (function () { var host = D.getElementById('tiext-design-settings'), sels = host ? host.querySelectorAll('select') : [], s = sels[sels.length - 1]; return s && s.options[s.selectedIndex] ? s.options[s.selectedIndex].text : ''; })();
    var overhang = roof.overhangMode === 'none' || n(roof.overhangMm) === 0 ? 'None' : mm(roof.effectiveStructuralOverhangMm || roof.overhangMm);
    out.push({ title: 'Roof', go: function () { shellRow('exterior', '[data-ta-tool-open="roof"]'); }, rows: [
      row('Type', ROOF_TYPES[roof.family] || (roof.family ? String(roof.family).replace(/-/g, ' ') : selText('#tiext-style'))),
      row('Pitch', isFinite(Number(roof.pitchDeg)) ? (Math.round(n(roof.pitchDeg) * 10) / 10) + '°' : ''),
      row('Overhang', overhang),
      row('Colour', colour.replace(/\s*·\s*/, ' · '))
    ] });

    /* Exterior */
    out.push({ title: 'Exterior', go: function () { shellRow('exterior', '[data-ta-tool-open="style"]'); }, rows: [
      row('Building type', selText('#tiext-style')),
      row('Cladding colour', selText('#tiext-scheme'))
    ] });

    /* Windows and doors */
    var WALL = { front: 'Front wall', back: 'Back wall', left: 'Left wall', right: 'Right wall' };
    var oc = Array.isArray(d.openingCollection) ? d.openingCollection : [];
    var ow = oc.filter(function (o) { return o && o.enabled !== false; }).sort(function (a, b) { return (a.type === 'door' ? 0 : 1) - (b.type === 'door' ? 0 : 1) || String(a.id).localeCompare(String(b.id)); });
    var wn = 0, openRows = ow.map(function (o) {
      if (o.type === 'door') return row('Door', mm2(o.frameWidthMm, o.frameHeightMm), [WALL[o.wall], o.swing === 'outward' ? 'opens outwards' : '', 'frame size'].filter(Boolean).join(' · '));
      wn++;
      return row('Window ' + wn, mm2(o.frameWidthMm, o.frameHeightMm), [WALL[o.wall], 'sill ' + Math.round(n(o.sillHeightMm)) + ' mm', 'frame size'].filter(Boolean).join(' · '));
    });
    if (!wn) openRows.push(row('Windows', 'None'));
    out.push({ title: 'Windows and doors', go: function () { var Ed = W.ThermoAspenWindowsEditorV1; shellTab('layout'); if (Ed && Ed.open) Ed.open(); }, rows: openRows });

    /* Stove */
    var stove = core && core.assembly && core.assembly.stove, modelId = stove && stove.modelId || (d.project && d.project.heater && (d.project.heater.preferredModelId || d.project.heater.modelId));
    var M = modelId && Cat && Cat.getModel ? tryGet(function () { return Cat.getModel(modelId); }) : null, id = M && M.identity || {}, sz = M && M.sizing || {}, ph = M && M.physical || {};
    var prot = core && core.assembly && core.assembly.protection, pItems = prot && prot.items || [];
    var shield = pItems.map(function (it) { var s = it.system && it.system.identity; return s ? [s.manufacturer, s.model].filter(Boolean).join(' ') : ''; }).filter(Boolean).join(', ');
    var flueId = d.project && d.project.heater && d.project.heater.flue && d.project.heater.flue.flueSystemId, F = flueId && Cat && Cat.getFlueSystem ? tryGet(function () { return Cat.getFlueSystem(flueId); }) : null;
    var flueName = F && F.identity ? [F.identity.manufacturer, F.identity.model].filter(Boolean).join(' ') : '';
    var fuel = id.fuel === 'electric' ? 'Electric' : id.fuel === 'wood' ? 'Wood-burning' : '';
    out.push({ title: 'Stove', go: function () { shellRow('layout', '[data-ta-layout-open="stove"]'); }, rows: M ? [
      row('Type', fuel),
      row('Brand', id.manufacturer),
      row('Model', id.model, id.itemNumber ? 'item ' + id.itemNumber : ''),
      row('Output', isFinite(Number(sz.nominalOutputKw)) ? sz.nominalOutputKw + ' kW' : ''),
      row('Suits rooms of', sz.saunaVolumeMinM3 && sz.saunaVolumeMaxM3 ? sz.saunaVolumeMinM3 + '–' + sz.saunaVolumeMaxM3 + ' m³' : '', eff > 0 ? 'your effective volume: ' + m3(eff) : ''),
      row('Size', ph.widthMm && ph.depthMm && ph.heightMm ? Math.round(ph.widthMm) + ' × ' + Math.round(ph.depthMm) + ' × ' + Math.round(ph.heightMm) + ' mm' : '', 'width × depth × height'),
      row('Weight', ph.netWeightKg ? ph.netWeightKg + ' kg' : '', ph.stoneCapacityKg ? 'plus ' + ph.stoneCapacityKg + ' kg of stones' : ''),
      row('Heat shield', shield || (prot && prot.configurationId && /unshielded/.test(prot.configurationId) ? 'None' : '')),
      row('Flue', M.flue && M.flue.nominalDiameterMm ? 'Ø ' + M.flue.nominalDiameterMm + ' mm' : '', flueName),
      row('Position', stove && WALL[stove.wall] || '')
    ] : [row('Stove', 'Not selected yet')] });

    /* Inside */
    var bs = tryGet(function () { return W.__ThermoAspenDesignerV5.getBenchDesignState(); }) || {};
    var mode = String(bs.mode || ''), joint = d.benchCornerJointStyle || (d.bench && d.bench.cornerJointStyle) || '';
    var JOINTS = { butt: 'Butt joints', interlocked: 'Interlocked', mitred: 'Mitred 45°' };
    var lining = d.cladding && d.cladding.wall;
    out.push({ title: 'Inside', go: function () { shellRow('layout', '[data-ta-layout-open="benches"]'); }, rows: [
      row('Benches', mode === 'none' ? 'None' : mode ? mode + ' tiers' : ''),
      mode && mode !== 'none' ? row('Bench layout', bs.arrangement === 'straight' ? 'Straight' : bs.arrangement ? 'Automatic' : '') : null,
      mode && mode !== 'none' ? row('Corner joints', JOINTS[joint] || '') : null,
      row('Wall lining', lining && lining.profile ? lining.profile : '', lining && lining.direction ? String(lining.direction).toLowerCase() + ' boards' : '')
    ] });

    out.forEach(function (s) { s.rows = s.rows.filter(Boolean); });
    return out.filter(function (s) { return s.rows.length; });
  }
  function shellTab(tab) { var A = W.ThermoAspenDesignerShell; if (A && A.showTab) A.showTab(tab); }
  function shellRow(tab, sel) {
    shellTab(tab);
    setTimeout(function () { var b = D.querySelector(sel); if (b && !b.disabled) b.click(); }, 60);
  }

  /* ---------- the embedded construction view ---------- */
  var EMBED_CSS = [
    'html,body{margin:0!important;padding:0!important;background:#fff!important;overflow:hidden!important}',
    'body>*:not(#ta-structural-wall-3d){display:none!important}',
    '#ta-structural-wall-3d{position:fixed!important;inset:0!important;margin:0!important;padding:0!important;width:auto!important;max-width:none!important;border:0!important;box-shadow:none!important;z-index:1}',
    '#ta-structural-wall-3d .ta3-card{height:100%;display:flex;flex-direction:column;border:0!important;padding:0!important;margin:0!important;box-shadow:none!important;border-radius:0!important;background:#fff!important}',
    '#ta-structural-wall-3d .ta3-head,#ta-structural-wall-3d .ta3-stepbar,#ta-structural-wall-3d .ta3-chevron{display:none!important}',
    '#ta-structural-wall-3d .ta3-main{flex:1;display:block!important;position:relative;min-height:0}',
    '#ta-structural-wall-3d .ta3-stage{position:absolute!important;inset:0!important;height:auto!important;width:auto!important;border-radius:0!important;border:0!important}',
    '#ta-structural-wall-3d .ta3-inspector{display:none!important}'
  ].join('\n');

  function setStatus(kind, text) {
    if (!ui.status) return;
    ui.status.hidden = !kind;
    ui.status.className = 'ta-sum-status' + (kind ? ' is-' + kind : '');
    ui.statusText.textContent = text || '';
    ui.retry.hidden = kind !== 'error';
  }
  function destroyFrame() {
    if (S.mode === 'inside') setMode('outside');
    S.inside.data = null; S.inside.key = null;
    clearTimeout(S.loadTimer); S.loadTimer = 0;
    if (S.observer) { try { S.observer.disconnect(); } catch (e) {} S.observer = null; }
    if (S.frame) { try { S.frame.src = 'about:blank'; } catch (e) {} if (S.frame.parentNode) S.frame.parentNode.removeChild(S.frame); }
    S.frame = null; S.viewer = null; S.doc = null; S.steps = []; S.index = -1;
    setPicked('');
  }
  /* 1.3.1: the Construction page is heavy and can take well over 30 s on a phone. The view is
     now picked up as soon as the page's viewer exists (about half the time of waiting for the whole page),
     the wait is up to 2 minutes with a progress count, and once loaded it is kept: coming
     back to Summary shows it at once unless the design has changed since. */
  function designKey() {
    var A = W.__ThermoAspenDesignerV5, s = null;
    try { s = A && A.getDesignState ? A.getDesignState() : null; } catch (e) { s = null; }
    try { return s ? JSON.stringify(s) : String((W.ThermoAspenSaunaDesignerData || {}).revision || ''); } catch (e) { return ''; }
  }
  /* 1.3.2: open the Construction page in the SAME theme as this page. In the theme editor or a
     theme preview, a plain link can open the published theme instead, whose Construction page
     may not have this 3D view. */
  function frameUrl() {
    var q = ['ta_embed=summary', 't=' + Date.now()], th = W.Shopify && W.Shopify.theme, sp = null;
    try { sp = new URLSearchParams(W.location.search); } catch (e) { sp = null; }
    var pid = sp && sp.get('preview_theme_id');
    if (!pid && th && th.id && (th.role !== 'main' || W.Shopify.designMode)) pid = String(th.id);
    if (pid) q.push('preview_theme_id=' + encodeURIComponent(pid));
    return CONSTRUCTION_URL + '?' + q.join('&');
  }
  function frameProblem(f) {
    var here = W.Shopify && W.Shopify.theme, there = null;
    try { there = f.contentWindow.Shopify && f.contentWindow.Shopify.theme; } catch (e) { there = null; }
    if (here && there && String(here.id) !== String(there.id)) return 'The construction view opened in another theme (“' + (there.name || there.id) + '”), which doesn’t have it.';
    return 'The construction view couldn’t be loaded.';
  }
  /* 1.5.0: keep only what the 3D view needs. The page is parsed without running anything
     (DOMParser), trimmed, and written back out. If anything goes wrong the full page is used. */
  function leanPage(html) {
    try {
      if (!W.DOMParser) return html;
      var doc = new W.DOMParser().parseFromString(html, 'text/html');
      if (!doc || !doc.body || !doc.getElementById('ta-structural-wall-3d')) return html;
      var TEMPLATE = /^shopify-section-template--/;
      function inTemplate(n) { var p = n.closest && n.closest('[id^="shopify-section-"]'); return !!(p && TEMPLATE.test(p.id)); }
      Array.prototype.slice.call(doc.head.querySelectorAll('script')).forEach(function (n) { n.parentNode.removeChild(n); });
      Array.prototype.slice.call(doc.head.querySelectorAll('link[rel="modulepreload"],link[rel="preconnect"],link[rel="dns-prefetch"],link[rel="compression-dictionary"],link[rel="ucp"],link[as="font"],link[rel="preload"]')).forEach(function (n) { n.parentNode.removeChild(n); });
      Array.prototype.slice.call(doc.body.querySelectorAll('[id^="shopify-section-"]')).forEach(function (n) { if (!TEMPLATE.test(n.id) && n.parentNode) n.parentNode.removeChild(n); });
      Array.prototype.slice.call(doc.body.querySelectorAll('script')).forEach(function (n) { if (!inTemplate(n) && n.parentNode) n.parentNode.removeChild(n); });
      Array.prototype.slice.call(doc.body.querySelectorAll('header,footer,#shopify-pc__banner,.shopify-pc__banner__dialog')).forEach(function (n) { if (!inTemplate(n) && n.parentNode) n.parentNode.removeChild(n); });
      return '<!doctype html>' + doc.documentElement.outerHTML;
    } catch (e) {
      try { console.warn('ThermoAspen Summary: page slimming skipped', e); } catch (e2) {}
      return html;
    }
  }
  function loadFrame() {
    destroyFrame();
    var gen = ++S.generation, started = Date.now();
    var H = W.ThermoAspenProjectHandoff;
    if (H && typeof H.captureCurrent === 'function') { try { H.captureCurrent(); } catch (e) {} }
    S.key = designKey();
    setStatus('loading', 'Preparing the construction view…');
    renderSteps();
    var f = D.createElement('iframe');
    f.className = 'ta-sum-iframe';
    f.title = '3D construction view';
    f.setAttribute('tabindex', '0');
    S.frame = f;
    ui.frameBox.insertBefore(f, ui.frameBox.firstChild);
    /* 1.4.0: on the shop's own domain Shopify forbids showing its pages inside a frame
       (X-Frame-Options: DENY), so a frame pointing at the Construction page is always blocked
       there; it only worked on preview links. The page is fetched instead and its HTML is
       given to the frame directly (srcdoc), which those headers don't apply to. Same origin,
       so it reads the same saved design. */
    fetch(frameUrl(), { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (html) {
      if (gen !== S.generation || S.frame !== f) return;
      var base = '<base href="' + W.location.origin + CONSTRUCTION_URL + '">';
      var early = '<style>' + EMBED_CSS + '</style>';
      html = leanPage(html);
      html = /<head[^>]*>/i.test(html) ? html.replace(/<head([^>]*)>/i, '<head$1>' + base + early) : base + early + html;
      f.srcdoc = html;
    }).catch(function (err) {
      if (gen !== S.generation) return;
      clearTimeout(S.loadTimer); S.loadTimer = 0;
      setStatus('error', 'The construction view couldn’t be loaded. Check your connection and try again.');
      try { console.warn('ThermoAspen Summary: construction page fetch failed', err); } catch (e) {}
    });
    var styled = false;
    (function poll() {
      if (gen !== S.generation || !S.frame) return;
      var doc = null, host = null, V = null, steps = [];
      try { doc = f.contentDocument; } catch (e) { doc = null; }
      if (doc && doc.URL && doc.URL !== 'about:blank' && doc.body) {
        host = doc.getElementById('ta-structural-wall-3d');
        if (host && !styled) {
          var st = doc.createElement('style'); st.textContent = EMBED_CSS; (doc.head || doc.documentElement).appendChild(st);
          doc.body.appendChild(host); styled = true; S.doc = doc;
        }
        if (!host && doc.readyState === 'complete') { setStatus('error', frameProblem(f)); try { console.warn('ThermoAspen Summary: no #ta-structural-wall-3d at', doc.URL); } catch (e) {} return; }
        try { V = f.contentWindow.__ThermoAspenStructuralFrameViewerV6; steps = V && V.getSteps ? V.getSteps() : []; } catch (e) { V = null; steps = []; }
        if (V && steps.length) {
          S.viewer = V; S.steps = steps;
          goStep(fullestIndex());
          if (S.mode === 'inside' && S.insidePending) { S.insidePending = false; enterInside(); renderSteps(); }
          watchSelection();
          setStatus('', '');
          return;
        }
      }
      var secs = Math.round((Date.now() - started) / 1000);
      if (Date.now() - started > LOAD_TIMEOUT_MS) { setStatus('error', doc && doc.readyState === 'complete' ? frameProblem(f) : 'The construction view is taking too long to load.'); return; }
      if (secs >= 4) ui.statusText.textContent = 'Building your sauna in 3D… ' + secs + ' s';
      S.loadTimer = setTimeout(poll, 300);
    })();
  }

  /* ---------- 1.6.0 Inside: the Interior viewer in build-up mode ---------- */
  var INSIDE_STEPS = [
    { id: 'frame', label: 'Frame with wall and roof OSB', layers: ['@structure', 'floor_sheathing_v1', 'wall_sheathing_v1', 'roof_rafters_v1', 'flat_roof_joists_v1', 'flat_roof_firrings_v1', 'ceiling_timbers_v1', 'larch_roof_boards_v1', 'flat_roof_deck_v1'], own: ['frames'] },
    { id: 'wall-insulation', label: 'Wall insulation', layers: ['wall_insulation_v1'] },
    { id: 'ceiling-insulation', label: 'Ceiling insulation', layers: ['ceiling_insulation_v1'] },
    { id: 'vcl', label: 'Vapour control layer (foil)', layers: ['interior_vcl_v1'] },
    { id: 'ceiling-counter-battens', label: 'Ceiling counter battens', layers: ['ceiling_counter_battens_v1'] },
    { id: 'ceiling-battens', label: 'Ceiling battens', layers: ['ceiling_battens_v1'] },
    { id: 'counter-battens', label: 'Wall counter battens', layers: ['interior_counter_battens_v1'] },
    { id: 'battens', label: 'Wall battens', layers: ['interior_battens_v1'] },
    { id: 'ceiling-cladding', label: 'Ceiling cladding', own: ['ceiling'] },
    { id: 'wall-cladding', label: 'Wall cladding and floor', own: ['walls', 'floor'] },
    { id: 'window-boards', label: 'Window boards and linings', own: ['linings'] },
    { id: 'benches', label: 'Benches', own: ['benches'] },
    { id: 'backrests', label: 'Backrests', own: ['backrests'] },
    { id: 'stove', label: 'Stove', own: ['stove'] }
  ];
  var OWN_KEYS = ['walls', 'ceiling', 'floor', 'frames', 'linings', 'benches', 'backrests', 'stove'];
  function interiorViewer() { var V = W.__ThermoAspen3DPreviewV17; return V && typeof V.setBuildUp === 'function' ? V : null; }
  /* Colours: the Outside view's own colour for each member (from its meshes at the fullest step), then the
     member's displayColor, then a colour by assembly type. */
  var TYPE_COLOURS = { 'wall-insulation': '#d6c99f', 'ceiling-insulation': '#dfd4ad', 'wall-sheathing': '#aa835c', 'floor-deck': '#9c744c', 'roof-deck': '#c9ad7a', 'vapour-control-layer': '#c9ced1', 'interior-battens': '#c2a878' };
  function colourOf(rec, x, cmap) {
    var id = x && x.id, a = x && x.assembly || {}, md = x && x.metadata || {};
    var c = (id && cmap[id]) || md.displayColor || rec.color || TYPE_COLOURS[a.assemblyType || a.type] || '#c8b48c';
    return c;
  }
  function meshOf(rec, x, cmap) {
    if (!rec || !rec.vertices || !rec.faces) return null;
    return { v: rec.vertices.map(function (p) { return [n(p.x), n(p.y), n(p.z)]; }), f: rec.faces, c: colourOf(rec, x, cmap || {}), o: rec.opacity == null ? 1 : n(rec.opacity, 1) };
  }
  /* Meshes for every construction layer used Inside, from the Outside view's construction model. */
  function harvest() {
    var fw = null; try { fw = S.frame && S.frame.contentWindow; } catch (e) { fw = null; }
    var RC = fw && fw.ThermoAspenConstructionRendererCoreV1 && fw.ThermoAspenConstructionRendererCoreV1.geometry, V = S.viewer;
    if (!RC || !V) return null;
    var out = tryGet(function () { return V.getOutput(); }) || {}, cm = tryGet(function () { return V.getConstructionModel(); }) || {};
    var layers = {}, byId = {}, cmap = {};
    var L = cm.layers || {}, src = L.byId && typeof L.byId === 'object' ? Object.keys(L.byId).map(function (k) { return L.byId[k]; }) : (L.layers || []);
    src.forEach(function (l) { if (l && l.id && !byId[l.id]) byId[l.id] = l; });
    (tryGet(function () { return V.getMeshes(); }) || []).forEach(function (r) { var id = r && r.member && r.member.id; if (id && r.color && !cmap[id]) cmap[id] = r.color; });
    function add(id, list, kind) { var a = layers[id] || (layers[id] = []); (list || []).forEach(function (x) { var r = null; try { r = kind === 'panel' ? RC.panel(x) : RC.structuralMember(x); } catch (e) { r = null; } var m = meshOf(r, x, cmap); if (m) a.push(m); }); }
    add('@structure', out.structuralMembers, 'member');
    INSIDE_STEPS.forEach(function (st) { (st.layers || []).forEach(function (id) { if (id === '@structure' || layers[id]) return; var l = byId[id]; if (!l) return; add(id, l.members, 'member'); add(id, l.panels, 'panel'); }); });
    return layers;
  }
  function hasBackrests() { var g = W.ThermoAspenSaunaSceneData && W.ThermoAspenSaunaSceneData.benchGeometry; return !!(g && (g.members || []).some(function (m) { return /backrest/.test(String(m && (m.assemblyType || m.purpose) || '')); })); }
  function insideStepList(data) {
    return INSIDE_STEPS.filter(function (st) {
      if (st.id === 'backrests') return hasBackrests();
      if (!st.layers) return true;
      return st.layers.some(function (id) { return data[id] && data[id].length; });
    });
  }
  function insideView(i) {
    var v = { own: {}, layers: {} }, list = S.inside.steps;
    OWN_KEYS.forEach(function (k) { v.own[k] = false; });
    for (var k = 0; k <= i && k < list.length; k++) {
      (list[k].own || []).forEach(function (o) { v.own[o] = true; });
      (list[k].layers || []).forEach(function (id) { v.layers[id] = true; });
    }
    return v;
  }
  function insideGo(i) {
    var n0 = S.inside.steps.length, V3 = interiorViewer(); if (!n0 || !V3) return;
    S.inside.index = ((i % n0) + n0) % n0;
    V3.setBuildUpView(insideView(S.inside.index));
    renderSteps();
  }
  function renderInsideSteps() {
    var list = S.inside.steps, ready = list.length > 0, st = ready ? list[S.inside.index] : null;
    ui.prev.disabled = !ready || list.length < 2; ui.next.disabled = !ready || list.length < 2;
    ui.stepTitle.textContent = st ? st.label : '';
    ui.stepMeta.textContent = st ? 'Step ' + (S.inside.index + 1) + ' of ' + list.length : '';
  }
  function setModeButtons() {
    if (!ui.modeOut) return;
    ui.modeOut.setAttribute('aria-pressed', S.mode === 'outside' ? 'true' : 'false');
    ui.modeIn.setAttribute('aria-pressed', S.mode === 'inside' ? 'true' : 'false');
    if (ui.frameBox) ui.frameBox.classList.toggle('is-inside', S.mode === 'inside');
  }
  function enterInside() {
    var V3 = interiorViewer();
    if (!V3) { setStatus('error', 'The interior view is not available on this page.'); return false; }
    if (!S.viewer) { setStatus('loading', 'Preparing the inside view…'); S.insidePending = true; return true; }
    if (!S.inside.data || S.inside.key !== S.key) {
      var data = harvest(); if (!data) { setStatus('error', 'The construction layers could not be read.'); return false; }
      S.inside.data = data; S.inside.key = S.key; S.inside.steps = insideStepList(data); S.inside.index = 0;
    }
    var stage = V3.getStage && V3.getStage();
    if (stage && !S.inside.home) { S.inside.home = { parent: stage.parentNode, next: stage.nextSibling }; ui.frameBox.appendChild(stage); }
    try { V3.ensureStarted(); } catch (e) {}
    V3.setBuildUpView(insideView(S.inside.index));
    V3.setBuildUp({ layers: S.inside.data });
    setStatus('', ''); setPicked('');
    try { W.dispatchEvent(new Event('resize')); } catch (e) {}
    return true;
  }
  function leaveInside() {
    var V3 = interiorViewer(); S.insidePending = false;
    if (V3) { try { V3.clearBuildUp(); } catch (e) {} }
    var h = S.inside.home, stage = V3 && V3.getStage && V3.getStage();
    if (h && stage && h.parent) { if (h.next && h.next.parentNode === h.parent) h.parent.insertBefore(stage, h.next); else h.parent.appendChild(stage); }
    S.inside.home = null;
    try { W.dispatchEvent(new Event('resize')); } catch (e) {}
  }
  function setMode(m) {
    m = m === 'inside' ? 'inside' : 'outside';
    if (m === S.mode) return;
    if (S.mode === 'inside') leaveInside();
    S.mode = m; setModeButtons();
    if (m === 'inside' && !enterInside()) { S.mode = 'outside'; setModeButtons(); }
    renderSteps();
  }
  function wrapIndex(i) { var n = S.steps.length; return n ? ((i % n) + n) % n : 0; }
  function goStep(i) {
    if (!S.viewer || !S.steps.length) return;
    i = Math.max(0, Math.min(S.steps.length - 1, i));
    try { S.viewer.setStage(i); } catch (e) {}
    try { S.index = S.viewer.getStageIndex(); } catch (e) { S.index = i; }
    setPicked('');
    renderSteps();
  }
  // 1.4.2: the step with the most visible layers (the latest one on a tie) = the finished build.
  function fullestIndex() {
    var best = S.steps.length - 1, most = -1;
    for (var i = 0; i < S.steps.length; i++) {
      var v = S.steps[i] && S.steps[i].visibility, o = v && (v.layers || v), c = 0;
      if (Array.isArray(o)) c = o.length;
      else if (o && typeof o === 'object') for (var k in o) if (o[k] && o[k].visible !== false) c++;
      if (c >= most) { most = c; best = i; }
    }
    return best;
  }
  function milestoneIndex(m) {
    if (!m.id) return fullestIndex();
    for (var i = 0; i < S.steps.length; i++) if (S.steps[i] && S.steps[i].id === m.id) return i;
    return -1;
  }
  function watchSelection() {
    var box = S.doc && S.doc.getElementById('ta3-inspect'); if (!box || !('MutationObserver' in W)) return;
    S.observer = new MutationObserver(function () {
      var t = box.querySelector('.ta3-piece-title strong');
      setPicked(t ? t.textContent : '');
    });
    S.observer.observe(box, { childList: true, subtree: true, characterData: true });
  }
  function setPicked(name) {
    if (!ui.picked) return;
    ui.picked.hidden = !name;
    ui.pickedName.textContent = name || '';
  }

  /* ---------- UI ---------- */
  function build(host) {
    if (ui.root) return;
    var root = el('div', 'ta-sum' + (host ? ' ta-sum--inline' : '')); root.id = 'ta-designer-summary'; root.hidden = !host;
    if (!host) { root.setAttribute('role', 'dialog'); root.setAttribute('aria-modal', 'true'); root.setAttribute('aria-labelledby', 'ta-sum-title'); }
    var inner = el('div', 'ta-sum-inner'); root.appendChild(inner);

    var head = el('div', 'ta-sum-head');
    var title = el('h2', 'ta-sum-title', 'Your sauna'); title.id = 'ta-sum-title'; title.tabIndex = -1;
    ui.close = btn('ta-sum-close', '×', 'Close the summary');
    head.appendChild(title); head.appendChild(infoBtn('summary.design', 'About the summary')); head.appendChild(ui.close);
    inner.appendChild(head);

    // The 3D view, build steps and the specification list scroll together under the title.
    var scroll = el('div', 'ta-sum-scroll');
    var frameBox = el('div', 'ta-sum-frame'); ui.frameBox = frameBox;
    var status = el('div', 'ta-sum-status'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    status.appendChild(el('span', 'ta-sum-spinner')); ui.statusText = el('span', 'ta-sum-status-text'); status.appendChild(ui.statusText);
    ui.retry = btn('ta-sum-retry', 'Try again'); ui.retry.hidden = true; status.appendChild(ui.retry);
    ui.status = status; frameBox.appendChild(status);
    var picked = el('div', 'ta-sum-picked'); picked.hidden = true; picked.setAttribute('aria-live', 'polite');
    ui.pickedName = el('span', 'ta-sum-picked-name'); picked.appendChild(ui.pickedName); picked.appendChild(infoBtn('summary.frame', 'About the 3D construction view'));
    ui.picked = picked; frameBox.appendChild(picked);
    // 1.5.0: the 3D view and its step controls share one block, so on wide screens they can
    // stay in view beside the specification.
    var view = el('div', 'ta-sum-view');
    // 1.6.0: Outside (construction) / Inside (interior build-up) switch.
    var modes = el('div', 'ta-sum-modes'); modes.setAttribute('role', 'group'); modes.setAttribute('aria-label', 'View');
    ui.modeOut = btn('ta-sum-mode', 'Outside'); ui.modeIn = btn('ta-sum-mode', 'Inside');
    ui.modeOut.setAttribute('aria-pressed', 'true'); ui.modeIn.setAttribute('aria-pressed', 'false');
    modes.appendChild(ui.modeOut); modes.appendChild(ui.modeIn); view.appendChild(modes);
    view.appendChild(frameBox); scroll.appendChild(view);

    var steps = el('div', 'ta-sum-steps');
    var stepper = el('div', 'ta-sum-stepper');
    ui.prev = btn('ta-sum-arrow', ICON.prev, 'Previous build step'); ui.next = btn('ta-sum-arrow', ICON.next, 'Next build step');
    ui.stepLabel = el('span', 'ta-sum-step-label'); ui.stepLabel.setAttribute('aria-live', 'polite');
    ui.stepTitle = el('span', 'ta-sum-step-title'); ui.stepMeta = el('span', 'ta-sum-step-meta');
    ui.stepLabel.appendChild(ui.stepTitle); ui.stepLabel.appendChild(ui.stepMeta);
    stepper.appendChild(ui.prev); stepper.appendChild(ui.stepLabel); stepper.appendChild(ui.next);
    steps.appendChild(stepper);
    view.appendChild(steps);

    ui.specs = el('div', 'ta-sum-specs'); scroll.appendChild(ui.specs);
    inner.appendChild(scroll); ui.scroll = scroll;

    var foot = el('div', 'ta-sum-foot');
    ui.share = btn('ta-sum-share', ICON.share + '<span>Share</span>');
    ui.drawings = btn('ta-sum-drawings', '<span>Construction drawings</span>' + ICON.chev);
    foot.appendChild(ui.share); foot.appendChild(ui.drawings);
    inner.appendChild(foot);

    (host || D.body).appendChild(root); ui.root = root;

    ui.close.addEventListener('click', close);
    ui.retry.addEventListener('click', loadFrame);
    ui.prev.addEventListener('click', function () { if (S.mode === 'inside') insideGo(S.inside.index - 1); else goStep(wrapIndex(S.index - 1)); });
    ui.next.addEventListener('click', function () { if (S.mode === 'inside') insideGo(S.inside.index + 1); else goStep(wrapIndex(S.index + 1)); });
    ui.modeOut.addEventListener('click', function () { setMode('outside'); });
    ui.modeIn.addEventListener('click', function () { setMode('inside'); });
    ui.specs.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('[data-sec]'); if (!b) return;
      var s = S.sections && S.sections[Number(b.getAttribute('data-sec'))];
      close(); if (s && s.go) s.go();
    });
    ui.share.addEventListener('click', function () {
      if (!S.inline) close();
      var menu = D.querySelector('.ta-menu'); if (menu) menu.click();
      setTimeout(function () { var s = D.getElementById('ti-project-share'); if (s) s.click(); }, 60);
    });
    ui.drawings.addEventListener('click', function () {
      var H = W.ThermoAspenProjectHandoff;
      if (H && typeof H.goToConstruction === 'function') { H.goToConstruction(); return; }
      W.location.href = CONSTRUCTION_URL;
    });
    D.addEventListener('keydown', function (e) {
      if (!S.open || S.inline || e.key !== 'Escape') return;
      if (D.documentElement.classList.contains('ta-shell-sheet-open')) return;
      e.preventDefault(); close();
    });
  }
  function renderSpecs() {
    var secs = sections();
    ui.specs.innerHTML = '';
    S.sections = secs;
    secs.forEach(function (s, si) {
      var sec = el('section', 'ta-sum-group');
      var h = el('h3', 'ta-sum-group-title');
      var hb = el('button', 'ta-sum-group-link'); hb.type = 'button'; hb.setAttribute('data-sec', String(si));
      hb.appendChild(el('span', null, s.title));
      var c = el('span', 'ta-sum-group-chev'); c.setAttribute('aria-hidden', 'true'); c.innerHTML = ICON.chev; hb.appendChild(c);
      hb.setAttribute('aria-label', 'Change ' + s.title.toLowerCase());
      h.appendChild(hb); sec.appendChild(h);
      var dl = el('dl', 'ta-sum-list');
      s.rows.forEach(function (r) {
        var line = el('div', 'ta-sum-row');
        line.appendChild(el('dt', 'ta-sum-row-label', r.label));
        var dd = el('dd', 'ta-sum-row-value');
        dd.appendChild(el('span', 'ta-sum-row-main', r.value));
        if (r.sub) dd.appendChild(el('span', 'ta-sum-row-sub', r.sub));
        line.appendChild(dd); dl.appendChild(line);
      });
      sec.appendChild(dl); ui.specs.appendChild(sec);
    });
  }
  function renderSteps() {
    if (S.mode === 'inside') { renderInsideSteps(); return; }
    var ready = !!(S.viewer && S.steps.length);
    ui.prev.disabled = !ready || S.steps.length < 2;
    ui.next.disabled = !ready || S.steps.length < 2;
    var s = ready ? S.steps[S.index] : null;
    ui.stepTitle.textContent = s ? (s.label || s.title || '') : '';
    ui.stepMeta.textContent = s ? 'Step ' + (S.index + 1) + ' of ' + S.steps.length : '';
  }

  /* ---------- open / close ---------- */
  function mount(host) {
    if (!host) return false;
    if (ui.root && !S.inline) { if (S.open) close(); ui.root.parentNode.removeChild(ui.root); ui = {}; }
    S.inline = true;
    build(host);
    return true;
  }
  function open() {
    build();
    if (S.open) { renderSpecs(); return true; }
    S.returnFocus = D.activeElement;
    S.open = true; ui.root.hidden = false;
    if (!S.inline) D.documentElement.classList.add('ta-sum-open');
    renderSpecs(); renderSteps();
    ui.scroll.scrollTop = 0;
    if (S.inline && S.frame && (S.viewer || S.loadTimer) && S.key === designKey()) return true;
    loadFrame();
    if (!S.inline) try { ui.root.querySelector('.ta-sum-title').focus({ preventScroll: true }); } catch (e) {}
    return true;
  }
  function close() {
    if (!ui.root || !S.open) return;
    if (S.mode === 'inside') setMode('outside');
    S.open = false;
    // Inline: keep the loaded (or loading) view for the next visit; it is reloaded only if the design changes.
    if (S.inline) return;
    S.generation++;
    destroyFrame();
    ui.root.hidden = true;
    D.documentElement.classList.remove('ta-sum-open');
    if (S.returnFocus && S.returnFocus.focus) try { S.returnFocus.focus({ preventScroll: true }); } catch (e) {}
  }
  // Keep the specification current while the Summary is showing.
  W.addEventListener('ThermoAspenSaunaDesignerUpdated', function () { if (S.open && ui.specs) setTimeout(renderSpecs, 0); });

  W.ThermoAspenDesignerSummaryV1 = { version: VERSION, mount: mount, open: open, close: close, isOpen: function () { return S.open; } };
  try { W.dispatchEvent(new CustomEvent('ThermoAspenDesignerSummaryReady', { detail: { version: VERSION } })); } catch (e) {}
})(window, document);
